import { Button, ButtonProps } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useLanguages } from "../../common/translations/useLanguages.js";
import { useUrlSearchParams } from "../commons/hooks/useUrlSearchParams.js";
import { ORGANISATION_TYPE_URL_SEARCH_PARAM } from "../constants.js";
import { buildRegisterUrlWithPrefill } from "./buildRegisterUrlWithPrefill.js";
import type { CreateableOrganisationType } from "./isValidOrganisationType.js";
import { useAuth } from "./useAuth.js";

interface Props {
  prefill?:
    | {
        email: string;
        firstName?: string;
        lastName?: string;
        salutation?: string;
      }
    | undefined;
  organisationType?: CreateableOrganisationType | undefined;
  /** Overrides keycloak-js' default scope of the registration URL (see `INVITATION_SCOPE`). */
  scope?: string | undefined;
  variant?: ButtonProps["variant"];
  color?: ButtonProps["color"];
  sx?: ButtonProps["sx"];
}

export const RegisterButton = ({
  prefill,
  organisationType,
  scope,
  variant = "contained",
  color = "secondary",
  sx,
}: Props) => {
  const { auth } = useAuth();
  const { t } = useTranslate(["Global"]);
  const { currentLanguage } = useLanguages();
  const { setUrlSearchParams } = useUrlSearchParams();

  const handleClick = React.useCallback(() => {
    if (organisationType) {
      setUrlSearchParams({
        [ORGANISATION_TYPE_URL_SEARCH_PARAM]: organisationType,
      });
    }

    setTimeout(async () => {
      try {
        const registerUrl = await auth.createRegisterUrl({
          locale: currentLanguage.locale,
          ...(scope ? { scope } : {}),
        });
        window.open(buildRegisterUrlWithPrefill(registerUrl, prefill), "_self");
      } catch (e) {
        Sentry.captureException(e);
      }
    });
  }, [
    auth,
    currentLanguage.locale,
    organisationType,
    prefill,
    scope,
    setUrlSearchParams,
  ]);

  return (
    <Button
      variant={variant}
      color={color}
      disableElevation
      fullWidth
      sx={sx}
      data-testid="signup-register"
      onClick={handleClick}
    >
      {t("Create account", { ns: "Global" })}
    </Button>
  );
};
